import {ControllerFlowAPI, IHttpClient} from '@wix/yoshi-flow-editor'
import {
  queryExperiences,
  getExperience as getExperienceApi,
} from '@wix/ambassador-table-reservations-v1-experience/http'
import {CursorPaging, Experience} from '@wix/ambassador-table-reservations-v1-experience/types'

import {
  getDataFromLocalStorage,
  getDataFromMemoryCache,
  setDataToMemoryCache,
} from '../utils/dataCapsule'
import {setSsrWarmupData, getSsrWarmupData} from '../utils/warmupData'
import {ClientWithErrorHandler} from '../utils/errors'

const CACHE_KEY = 'tr-experiences'
const SSR_WARMUP_KEY = 'tr-experiences-ssr-warmup'

const MAX_REQUESTS_NUMBER = 3

export const fetchPaginatedExperiences = async (
  httpClient: IHttpClient,
  withErrorHandler: ClientWithErrorHandler,
): Promise<Experience[]> => {
  let allExperiences: Experience[] = []
  let cursor: CursorPaging['cursor']
  let requestCount = 0
  let hasNextPage = true

  while (hasNextPage && requestCount < MAX_REQUESTS_NUMBER) {
    const response = await withErrorHandler(
      async () =>
        httpClient.request(
          queryExperiences({
            query: {
              cursorPaging: {
                limit: 100,
                cursor,
              },
            },
          }),
        ),
      {
        errorCodesMap: {},
      },
    )

    if (!response?.data?.experiences) {
      break
    }

    const currentBatchExperiences = response.data.experiences
    allExperiences = [...allExperiences, ...currentBatchExperiences]

    cursor = response.data.pagingMetadata?.cursors?.next
    hasNextPage = Boolean(response.data.pagingMetadata?.hasNext)
    requestCount++
  }

  return allExperiences
}

const getExperiences = async (flowAPI: ControllerFlowAPI): Promise<Experience[]> => {
  const cachedExperiences = getDataFromMemoryCache<Experience[]>(flowAPI, CACHE_KEY)
  const ssrWarmupData = getSsrWarmupData<Experience[]>(flowAPI, SSR_WARMUP_KEY)

  // client cache
  if (cachedExperiences) {
    return cachedExperiences
    // ssr cache
  } else if (ssrWarmupData) {
    return ssrWarmupData
  } else {
    const allExperiences = await fetchPaginatedExperiences(
      flowAPI.httpClient,
      flowAPI.errorHandler.withErrorHandler,
    )

    setDataToMemoryCache(flowAPI, CACHE_KEY, allExperiences)
    setSsrWarmupData(flowAPI, SSR_WARMUP_KEY, allExperiences)
    return allExperiences
  }
}

const getExperience = async (
  flowAPI: ControllerFlowAPI,
  experienceId: string,
): Promise<Experience | undefined> => {
  const experienceFromCache = getDataFromLocalStorage<Experience>(flowAPI, experienceId)
  const {t} = flowAPI.translations

  if (experienceFromCache) {
    deserializeExperienceDates(experienceFromCache)
    return experienceFromCache
  } else {
    const res = await flowAPI.errorHandler.withErrorHandler(
      () => flowAPI.httpClient.request(getExperienceApi({experienceId})),
      {
        errorCodesMap: {
          applicationError: {
            NOT_FOUND: () => ({message: t('uou-experiences.shared.experience-not-found-error')}),
          },
        },
      },
    )

    return res?.data?.experience
  }
}

const deserializeExperienceDates = (experienceFromCache: Experience) => {
  if (experienceFromCache?.createdDate) {
    experienceFromCache.createdDate = new Date(experienceFromCache.createdDate)
  }

  if (experienceFromCache?.updatedDate) {
    experienceFromCache.updatedDate = new Date(experienceFromCache.updatedDate)
  }
}

export const experiencesService = {
  getExperiences,
  getExperience,
}
